import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search, FileText, ChevronRight, ChevronDown, MessageSquare, Check, User, Bot, ExternalLink, Upload, Link2, List, Share2, Copy, X, Mail } from 'lucide-react';
import { EmptyState } from './EmptyState';
interface Version {
  id: string;
  author: string;
  authorType: 'human' | 'agent';
  content: string;
  time: string;
  selected?: boolean;
}
interface Comment {
  author: string;
  authorType: 'human' | 'agent';
  text: string;
  time: string;
  resolved?: boolean;
}
interface Reference {
  title: string;
  url: string;
  type: 'web' | 'file';
}
type SectionStatus = 'empty' | 'draft' | 'review' | 'approved';
interface Section {
  id: string;
  heading: string;
  owner: string;
  status: SectionStatus;
  instruction?: string;
  bullets: string[];
  versions: Version[];
  comments: Comment[];
}
interface Doc {
  id: string;
  title: string;
  status: 'draft' | 'review' | 'final';
  instruction?: string;
  references?: Reference[];
  shareGroups?: string[];
  sourceType?: 'google-doc' | 'confluence' | 'upload';
  sourceUrl?: string;
  created: string;
  collaborators: string[];
  sections: Section[];
}
const PERMISSION_GROUPS = ['TMA Wide', 'Finance Group', 'Engineering', 'Leadership', 'External Partners'];
const DOCS: Doc[] = [{
  id: '1',
  title: 'API Rate Limiting Proposal',
  status: 'review',
  sourceType: 'confluence',
  sourceUrl: 'https://contextloops.atlassian.net/wiki/spaces/ENG/pages/123',
  created: 'Monday',
  collaborators: ['Matt', 'Tom', 'Finance Agent'],
  shareGroups: ['Engineering'],
  instruction: 'Technical proposal for implementing rate limiting on all external-facing API endpoints. Should cover background, approach, impact, and rollout. Aimed at engineering and product stakeholders.',
  references: [{
    title: 'Kong Rate Limiting Plugin Docs',
    url: 'https://docs.konghq.com/hub/kong-inc/rate-limiting-advanced/',
    type: 'web'
  }, {
    title: 'API Usage Dashboard',
    url: 'https://eu.posthog.com/project/1/dashboard/api-usage',
    type: 'web'
  }, {
    title: 'Current API Terms of Service',
    url: 'https://docs.google.com/document/d/tos123',
    type: 'file'
  }],
  sections: [{
    id: 's1',
    heading: 'Background',
    owner: 'Matt',
    status: 'review',
    instruction: 'Describe the problem, who is affected, and why action is needed now.',
    bullets: ['Current 429 error rates during peak hours', 'Which consumers are most affected', 'Why existing IP-based throttling is insufficient'],
    versions: [{
      id: 'v1',
      author: 'Matt',
      authorType: 'human',
      content: 'Third-party consumers of our /sync endpoint have been hitting 429 errors during peak hours (9-11am GMT). Current rate limiting is basic IP-based throttling at the load balancer level.',
      time: 'Monday',
      selected: true
    }, {
      id: 'v2',
      author: 'Legal Agent',
      authorType: 'agent',
      content: 'Third-party consumers of our /sync endpoint have been hitting 429 errors during peak hours (9-11am GMT). Current rate limiting is basic IP-based throttling at the load balancer level. Note: our API terms of service do not currently specify rate limits. This should be updated before enforcement.',
      time: 'Tuesday'
    }],
    comments: [{
      author: 'Legal Agent',
      authorType: 'agent',
      text: 'ToS needs updating before we enforce new limits. Added a note in my version.',
      time: 'Tuesday'
    }]
  }, {
    id: 's2',
    heading: 'Proposed Approach',
    owner: 'Tom',
    status: 'review',
    instruction: 'Detail the technical solution, including configuration, headers, and failure modes.',
    bullets: ['Algorithm choice and rationale', 'Per-key vs per-IP limits', 'Response headers for consumers', 'Failure mode if backing store is unavailable'],
    versions: [{
      id: 'v3',
      author: 'Tom',
      authorType: 'human',
      content: 'Implement token-bucket rate limiting at the API gateway level. 100 requests/minute per API key with a 10-request burst allowance. Rate limit headers in all responses. Retry-After header on 429s.',
      time: 'Monday',
      selected: true
    }, {
      id: 'v4',
      author: 'Zen Bot Project',
      authorType: 'agent',
      content: 'Implement token-bucket rate limiting at the Kong gateway using the rate-limiting-advanced plugin. Config: limit 100, window_size 60, strategy cluster. Include X-RateLimit-Limit, X-RateLimit-Remaining, and Retry-After headers. Fallback to 503 if Redis is unavailable.',
      time: 'Tuesday'
    }],
    comments: [{
      author: 'Matt',
      authorType: 'human',
      text: 'Good detail on the Kong config. What happens if Redis goes down?',
      time: 'Tuesday',
      resolved: true
    }, {
      author: 'Zen Bot Project',
      authorType: 'agent',
      text: 'Added Redis fallback to my version. Suggests 503 rather than open-gate.',
      time: 'Tuesday',
      resolved: true
    }, {
      author: 'Tom',
      authorType: 'human',
      text: 'Should we also consider sliding window as an alternative? Token bucket might be too permissive for burst abuse.',
      time: 'Wednesday'
    }]
  }, {
    id: 's3',
    heading: 'Impact Assessment',
    owner: 'Finance Agent',
    status: 'approved',
    instruction: 'Quantify the business impact of the proposed limits on existing consumers.',
    bullets: ['Percentage of requests affected', 'Which consumers are impacted', 'Revenue risk assessment'],
    versions: [{
      id: 'v5',
      author: 'Finance Agent',
      authorType: 'agent',
      content: 'Based on current API usage patterns, approximately 3% of requests would be rate-limited under the proposed 100 req/min limit. Affected consumers: Acme Corp (highest volume), DataSync Ltd. Revenue impact: minimal, both are on enterprise plans with SLA provisions for rate limiting.',
      time: 'Tuesday',
      selected: true
    }],
    comments: []
  }, {
    id: 's4',
    heading: 'Rollout Plan',
    owner: 'Tom',
    status: 'draft',
    instruction: 'Phased rollout with clear milestones and communication plan.',
    bullets: ['Monitoring-only phase', 'Consumer notification timeline', 'Enforcement date', 'Post-enforcement adjustment criteria'],
    versions: [{
      id: 'v6',
      author: 'Tom',
      authorType: 'human',
      content: 'Phase 1: Deploy with monitoring only (log 429s but do not enforce). Phase 2: Enforce with 2-week notice to API consumers. Phase 3: Reduce burst allowance based on observed patterns.',
      time: 'Wednesday'
    }],
    comments: [{
      author: 'Matt',
      authorType: 'human',
      text: 'Can we add a phase 0 where we update the ToS first?',
      time: 'Wednesday'
    }]
  }]
}, {
  id: '2',
  title: 'Q2 Board Report',
  status: 'draft',
  sourceType: 'google-doc',
  sourceUrl: 'https://docs.google.com/document/d/abc123',
  created: 'yesterday',
  collaborators: ['Matt', 'Sarah', 'Finance Agent'],
  shareGroups: ['Leadership'],
  instruction: 'Quarterly board report covering product progress, financial health, and key risks. Audience: board members, investors.',
  references: [{
    title: 'Q1 Board Report',
    url: 'https://docs.google.com/document/d/q1report',
    type: 'file'
  }, {
    title: 'Monthly Spend Dashboard',
    url: 'https://zenbot.chat/#/lists',
    type: 'web'
  }],
  sections: [{
    id: 's1',
    heading: 'Executive Summary',
    owner: 'Matt',
    status: 'draft',
    instruction: 'High-level overview of Q2 priorities and current status.',
    bullets: ['Three key priorities for the quarter', 'Status of each', 'One-line financial summary'],
    versions: [{
      id: 'v1',
      author: 'Finance Agent',
      authorType: 'agent',
      content: 'Q2 2026 focused on three priorities: shipping the Zen Bot UI redesign, establishing the Nango integration pipeline, and maintaining infrastructure spend below $5,000/month. All three are on track as of mid-April.',
      time: 'yesterday'
    }],
    comments: []
  }, {
    id: 's2',
    heading: 'Financial Overview',
    owner: 'Finance Agent',
    status: 'approved',
    instruction: 'Monthly run rate broken down by category with comparison to Q1.',
    bullets: ['Total monthly run rate', 'Infrastructure breakdown', 'AI/API costs', 'SaaS and contractor costs'],
    versions: [{
      id: 'v2',
      author: 'Finance Agent',
      authorType: 'agent',
      content: 'Monthly run rate: $4,200. Infrastructure: $1,200 (Hetzner $400, LiveKit $300, Vercel $80, misc $420). AI/APIs: $2,000 (OpenAI $1,200, Anthropic $600, other $200). SaaS tooling: $500. Contractor costs: $500.',
      time: 'yesterday',
      selected: true
    }],
    comments: []
  }, {
    id: 's3',
    heading: 'Product Progress',
    owner: 'Matt',
    status: 'empty',
    instruction: 'Key product milestones achieved and upcoming.',
    bullets: ['Major features shipped', 'Current sprint focus', 'Q2 delivery confidence'],
    versions: [],
    comments: []
  }, {
    id: 's4',
    heading: 'Key Risks',
    owner: 'Sarah',
    status: 'empty',
    instruction: 'Top 3-5 risks with mitigation strategies.',
    bullets: ['Technical risks', 'Market/competitive risks', 'Resource constraints'],
    versions: [],
    comments: []
  }]
}, {
  id: '3',
  title: 'Acme Partnership Agreement',
  status: 'final',
  sourceType: 'google-doc',
  sourceUrl: 'https://docs.google.com/document/d/def456',
  created: 'last week',
  collaborators: ['Matt', 'James', 'Legal Agent'],
  shareGroups: ['External Partners'],
  instruction: 'Partnership agreement between Context Loops Ltd and Acme Corp.',
  references: [{
    title: 'Standard Partnership Template',
    url: 'https://drive.google.com/file/template',
    type: 'file'
  }],
  sections: [{
    id: 's1',
    heading: 'Terms',
    owner: 'Legal Agent',
    status: 'approved',
    instruction: 'Core commercial terms.',
    bullets: ['Duration and renewal', 'Liability cap', 'Termination provisions'],
    versions: [{
      id: 'v1',
      author: 'Legal Agent',
      authorType: 'agent',
      content: 'Standard partnership terms with 12-month initial period. Liability cap adjusted to $100,000 per the April review. Termination: 60 days written notice.',
      time: 'last week',
      selected: true
    }],
    comments: []
  }, {
    id: 's2',
    heading: 'Data Processing',
    owner: 'Legal Agent',
    status: 'approved',
    instruction: 'GDPR-compliant data processing terms.',
    bullets: ['DPA reference', 'Data retention period', 'Processing jurisdiction', 'Sub-processors'],
    versions: [{
      id: 'v1',
      author: 'Legal Agent',
      authorType: 'agent',
      content: 'DPA attached as Appendix B. Data retention: 12 months after contract end. Processing jurisdiction: EU only. Sub-processors: Hetzner (infrastructure), Convex (database).',
      time: 'last week',
      selected: true
    }],
    comments: []
  }]
}];
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  review: 'In Review',
  final: 'Final'
};
const SECTION_STATUS_LABELS: Record<SectionStatus, string> = {
  empty: 'Empty',
  draft: 'Draft',
  review: 'In Review',
  approved: 'Approved'
};
const SECTION_STATUS_COLOURS: Record<SectionStatus, string> = {
  empty: 'text-[#8a8680]/30',
  draft: 'text-[#8a8680]/50',
  review: 'text-amber-400/60',
  approved: 'text-[#4a9a9a]/60'
};
export function DocumentsView() {
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  if (viewingId) {
    const doc = DOCS.find(d => d.id === viewingId);
    if (doc) return <DocEditor doc={doc} onBack={() => setViewingId(null)} data-id="element-822" />;
  }
  const filtered = DOCS.filter(d => !search || d.title.toLowerCase().includes(search.toLowerCase()));
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-823">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-824">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-825">Documents</h1>
        <div className="relative mb-8" data-id="element-826"><Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-827" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-828" /></div>
        <div className="flex flex-col gap-3" data-id="element-829">
          {filtered.map((doc, i) => {
          const unresolvedTotal = doc.sections.reduce((sum, s) => sum + s.comments.filter(c => !c.resolved).length, 0);
          return <motion.button key={doc.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3,
            delay: i * 0.04
          }} onClick={() => setViewingId(doc.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-830">
              <div className="flex items-center gap-4" data-id="element-831">
                <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-832"><FileText size={16} className="text-[#8a8680]/60" strokeWidth={1.5} data-id="element-833" /></div>
                <div className="flex-1 min-w-0" data-id="element-834">
                  <span className="text-[15px] text-[#e8e4df] truncate block" data-id="element-835">{doc.title}</span>
                  <div className="flex items-center gap-3 mt-1" data-id="element-836">
                    <span className="text-[11px] text-[#8a8680]/40" data-id="element-837">{STATUS_LABELS[doc.status]}</span>
                    <span className="text-[11px] text-[#8a8680]/40" data-id="element-838">{doc.sections.length} sections</span>
                    {unresolvedTotal > 0 && <span className="text-[11px] text-amber-400/50" data-id="element-839">{unresolvedTotal} unresolved</span>}
                    {doc.shareGroups && doc.shareGroups.length > 0 && <span className="text-[11px] text-[#8a8680]/30" data-id="element-840">shared</span>}
                  </div>
                </div>
                <ChevronRight size={16} className="text-[#8a8680]/20 group-hover:text-[#8a8680]/60 transition-colors shrink-0" data-id="element-841" />
              </div>
            </motion.button>;
        })}
          {filtered.length === 0 && <EmptyState message="No documents." data-id="element-842" />}
        </div>
        <div className="mt-8" data-id="element-843">
          <AnimatePresence data-id="element-844">
            {showImport && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden mb-4" data-id="element-845">
                <div className="bg-[#1c1c22] rounded-[16px] p-5 space-y-3" data-id="element-846">
                  <p className="text-[13px] text-[#8a8680]/60" data-id="element-847">Paste a link to a Google Doc, Confluence page, or upload a file</p>
                  <input type="url" value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://docs.google.com/... or https://confluence..." className="w-full bg-transparent text-[#e8e4df] text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 pb-2 font-mono" data-id="element-848" />
                  <div className="flex items-center gap-6 pt-2" data-id="element-849"><button onClick={() => setShowImport(false)} className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-850">Import</button><button className="text-[13px] text-[#8a8680]/50 hover:text-[#8a8680] flex items-center gap-1.5" data-id="element-851"><Upload size={12} data-id="element-852" />Upload file</button><button onClick={() => setShowImport(false)} className="text-[13px] text-[#8a8680]/50 hover:text-[#8a8680] ml-auto" data-id="element-853">Cancel</button></div>
                </div>
              </motion.div>}
          </AnimatePresence>
          {!showImport && <button onClick={() => setShowImport(true)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-854"><Plus size={13} data-id="element-855" />New document</button>}
        </div>
      </div>
    </div>;
}
function DocEditor({
  doc,
  onBack
}: {
  doc: Doc;
  onBack: () => void;
}) {
  const [expandedSection, setExpandedSection] = useState<string | null>(doc.sections[0]?.id || null);
  const [activeVersionTab, setActiveVersionTab] = useState<Record<string, string>>({});
  const [commentInputs, setCommentInputs] = useState<Record<string, boolean>>({});
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [showRefs, setShowRefs] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareGroups, setShareGroups] = useState<string[]>(doc.shareGroups || []);
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://zenbot.chat/d/${doc.id}`;
  const toggleGroup = (g: string) => setShareGroups(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const copyLink = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const getActiveVersion = (section: Section) => {
    const tabId = activeVersionTab[section.id];
    if (tabId) return section.versions.find(v => v.id === tabId);
    return section.versions.find(v => v.selected) || section.versions[0];
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-856">
      <div className="max-w-[700px] mx-auto w-full" data-id="element-857">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-858"><ArrowLeft size={14} data-id="element-859" /><span className="text-[13px]" data-id="element-860">Documents</span></button>

        <header className="mb-6" data-id="element-861">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-862">{doc.title}</h1>
          <div className="flex items-center gap-4 mt-2" data-id="element-863">
            <span className="text-[12px] text-[#8a8680]/50" data-id="element-864">{STATUS_LABELS[doc.status]}</span>
            {doc.sourceUrl && <a href={doc.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[12px] text-[#8a8680]/40 hover:text-[#4a9a9a] transition-colors" data-id="element-865"><ExternalLink size={10} data-id="element-866" />source</a>}
            <button onClick={() => setShowShare(!showShare)} className="flex items-center gap-1 text-[12px] text-[#8a8680]/40 hover:text-[#4a9a9a] transition-colors" data-id="element-867"><Share2 size={10} data-id="element-868" />share</button>
          </div>
          <div className="flex items-center gap-3 mt-2" data-id="element-869">{doc.collaborators.map(c => <span key={c} className="text-[12px] text-[#8a8680]/50" data-id="element-870">{c}</span>)}</div>
        </header>

        {/* Share panel */}
        <AnimatePresence data-id="element-871">
          {showShare && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden mb-6" data-id="element-872">
              <div className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-873">
                <div className="flex items-center gap-2 mb-4" data-id="element-874"><Mail size={12} className="text-[#8a8680]/50" data-id="element-875" /><span className="text-[12px] text-[#8a8680]/50" data-id="element-876">Comment access via email OTP</span></div>

                <div className="flex items-center justify-between mb-3" data-id="element-877">
                  <span className="text-[13px] text-[#8a8680]" data-id="element-878">Permission groups</span>
                  <div className="relative" data-id="element-879">
                    <button onClick={() => setShowGroupMenu(!showGroupMenu)} className="text-[#8a8680]/50 hover:text-[#8a8680]" data-id="element-880"><Plus size={13} data-id="element-881" /></button>
                    {showGroupMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowGroupMenu(false)} data-id="element-882" /><div className="absolute right-0 mt-2 rounded-lg z-20 w-[180px] bg-[#252530]" data-id="element-883">{PERMISSION_GROUPS.map(g => <button key={g} onClick={() => {
                      toggleGroup(g);
                      setShowGroupMenu(false);
                    }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#4a9a9a]/10" style={{
                      color: shareGroups.includes(g) ? '#4a9a9a' : '#e8e4df'
                    }} data-id="element-884">{g}</button>)}</div></>}
                  </div>
                </div>
                {shareGroups.length > 0 ? <div className="flex flex-wrap gap-2 mb-4" data-id="element-885">{shareGroups.map(g => <span key={g} className="inline-flex items-center gap-1.5 text-[12px] bg-[#252530] text-[#8a8680] px-2.5 py-1 rounded-full" data-id="element-886">{g}<button onClick={() => toggleGroup(g)} className="hover:text-[#e8e4df]" data-id="element-887"><X size={9} data-id="element-888" /></button></span>)}</div> : <p className="text-[12px] text-[#8a8680]/40 mb-4" data-id="element-889">No groups added. Anyone with the link can enter their email for OTP access.</p>}

                <div className="flex items-center gap-3" data-id="element-890">
                  <div className="flex-1 bg-[#252530] rounded-md px-3 py-1.5" data-id="element-891"><span className="text-[12px] text-[#8a8680]/50 font-mono" data-id="element-892">{shareUrl}</span></div>
                  <button onClick={copyLink} className="text-[12px] text-[#4a9a9a]/70 hover:text-[#4a9a9a] flex items-center gap-1" data-id="element-893">{copied ? <><Check size={11} data-id="element-894" />Copied</> : <><Copy size={11} data-id="element-895" />Copy</>}</button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {doc.instruction && <p className="text-[13px] text-[#8a8680]/60 leading-[1.6] mb-6 pl-1 border-l-2 border-[#8a8680]/10 ml-1" data-id="element-896">{doc.instruction}</p>}

        {doc.references && doc.references.length > 0 && <div className="mb-8" data-id="element-897">
            <button onClick={() => setShowRefs(!showRefs)} className="flex items-center gap-2 text-[12px] text-[#8a8680]/50 hover:text-[#8a8680] transition-colors mb-2" data-id="element-898"><Link2 size={11} data-id="element-899" />{doc.references.length} references <ChevronDown size={11} className={`transition-transform ${showRefs ? '' : '-rotate-90'}`} data-id="element-900" /></button>
            <AnimatePresence data-id="element-901">
              {showRefs && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden" data-id="element-902">
                  <div className="flex flex-col gap-1 pl-5" data-id="element-903">{doc.references.map((ref, i) => <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#8a8680]/50 hover:text-[#4a9a9a] transition-colors flex items-center gap-1.5 py-1" data-id="element-904">{ref.type === 'web' ? <ExternalLink size={9} data-id="element-905" /> : <FileText size={9} data-id="element-906" />}{ref.title}</a>)}</div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        <div className="flex flex-col gap-4" data-id="element-907">
          {doc.sections.map(section => {
          const isExpanded = expandedSection === section.id;
          const activeVersion = getActiveVersion(section);
          const hasVersions = section.versions.length > 0;
          const showCommentInput = commentInputs[section.id];
          const unresolvedComments = section.comments.filter(c => !c.resolved).length;
          return <div key={section.id} className="bg-[#1c1c22] rounded-[16px] overflow-hidden" data-id="element-908">
                <button onClick={() => setExpandedSection(isExpanded ? null : section.id)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-[#1e1e25] transition-colors" data-id="element-909">
                  <ChevronDown size={14} className={`text-[#8a8680]/40 transition-transform shrink-0 ${isExpanded ? '' : '-rotate-90'}`} data-id="element-910" />
                  <div className="flex-1 min-w-0" data-id="element-911"><span className="text-[15px] text-[#e8e4df]" data-id="element-912">{section.heading}</span></div>
                  <span className={`text-[11px] ${SECTION_STATUS_COLOURS[section.status]}`} data-id="element-913">{SECTION_STATUS_LABELS[section.status]}</span>
                  {unresolvedComments > 0 && <span className="text-[11px] text-amber-400/50" data-id="element-914">{unresolvedComments}</span>}
                  <span className="text-[11px] text-[#8a8680]/30" data-id="element-915">{section.owner}</span>
                </button>

                <AnimatePresence data-id="element-916">
                  {isExpanded && <motion.div initial={{
                height: 0
              }} animate={{
                height: 'auto'
              }} exit={{
                height: 0
              }} className="overflow-hidden" data-id="element-917">
                      <div className="px-5 pb-5" data-id="element-918">
                        {section.instruction && <p className="text-[12px] text-[#8a8680]/50 leading-[1.5] mb-3 pl-3 border-l border-[#8a8680]/10" data-id="element-919">{section.instruction}</p>}
                        {section.bullets.length > 0 && <div className="mb-4" data-id="element-920">{section.bullets.map((b, bi) => <div key={bi} className="flex items-start gap-2 py-0.5" data-id="element-921"><List size={10} className="text-[#8a8680]/30 mt-1 shrink-0" data-id="element-922" /><span className="text-[12px] text-[#8a8680]/50" data-id="element-923">{b}</span></div>)}</div>}

                        {!hasVersions ? <div className="py-6 text-center" data-id="element-924"><p className="text-[13px] text-[#8a8680]/40 mb-3" data-id="element-925">No content yet</p><button className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-926">Write first version</button></div> : <>
                            {section.versions.length > 1 && <div className="flex items-center gap-2 mb-4 flex-wrap" data-id="element-927">{section.versions.map(v => {
                        const isActive = (activeVersionTab[section.id] || section.versions.find(x => x.selected)?.id || section.versions[0]?.id) === v.id;
                        return <button key={v.id} onClick={() => setActiveVersionTab(prev => ({
                          ...prev,
                          [section.id]: v.id
                        }))} className={`flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full transition-colors ${isActive ? 'text-[#e8e4df] bg-[#252530]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-928">{v.authorType === 'agent' ? <Bot size={10} data-id="element-929" /> : <User size={10} data-id="element-930" />}{v.author}{v.selected && <Check size={9} className="text-[#4a9a9a]/70" data-id="element-931" />}</button>;
                      })}</div>}

                            {activeVersion && <div className="mb-4" data-id="element-932"><p className="text-[14px] text-[#e8e4df]/80 leading-[1.65]" data-id="element-933">{activeVersion.content}</p><div className="flex items-center gap-3 mt-3" data-id="element-934"><span className="text-[11px] text-[#8a8680]/40" data-id="element-935">{activeVersion.time}</span>{!activeVersion.selected && <button className="text-[11px] text-[#4a9a9a]/60 hover:text-[#4a9a9a]" data-id="element-936">select this version</button>}{activeVersion.selected && <span className="text-[11px] text-[#4a9a9a]/50" data-id="element-937">selected</span>}</div></div>}

                            {section.comments.length > 0 && <div className="border-t border-[#8a8680]/10 pt-4 mt-4" data-id="element-938">{section.comments.map((c, ci) => <div key={ci} className={`flex items-start gap-2.5 mb-3 ${c.resolved ? 'opacity-40' : ''}`} data-id="element-939"><div className="w-5 h-5 rounded-full bg-[#252530] flex items-center justify-center shrink-0 mt-0.5" data-id="element-940">{c.authorType === 'agent' ? <Bot size={9} className="text-[#8a8680]/60" data-id="element-941" /> : <User size={9} className="text-[#8a8680]/60" data-id="element-942" />}</div><div className="flex-1" data-id="element-943"><div className="flex items-center gap-2" data-id="element-944"><span className="text-[12px] text-[#8a8680]/60" data-id="element-945">{c.author}</span><span className="text-[11px] text-[#8a8680]/30" data-id="element-946">{c.time}</span>{c.resolved && <span className="text-[10px] text-[#4a9a9a]/40" data-id="element-947">resolved</span>}</div><p className={`text-[13px] leading-[1.5] mt-0.5 ${c.resolved ? 'text-[#8a8680]/40' : 'text-[#e8e4df]/70'}`} data-id="element-948">{c.text}</p></div>{!c.resolved && <button className="text-[10px] text-[#8a8680]/30 hover:text-[#4a9a9a] mt-1 shrink-0" data-id="element-949">resolve</button>}</div>)}</div>}

                            <div className="border-t border-[#8a8680]/10 pt-3 mt-3 flex items-center gap-4" data-id="element-950">
                              {!showCommentInput ? <><button onClick={() => setCommentInputs(prev => ({
                          ...prev,
                          [section.id]: true
                        }))} className="text-[12px] text-[#8a8680]/40 hover:text-[#8a8680] flex items-center gap-1.5" data-id="element-951"><MessageSquare size={11} data-id="element-952" />Comment</button><button className="text-[12px] text-[#8a8680]/40 hover:text-[#8a8680] flex items-center gap-1.5" data-id="element-953"><Plus size={11} data-id="element-954" />New version</button></> : <div className="flex items-center gap-3 w-full" data-id="element-955"><input type="text" value={commentTexts[section.id] || ''} onChange={e => setCommentTexts(prev => ({
                          ...prev,
                          [section.id]: e.target.value
                        }))} onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setCommentInputs(prev => ({
                              ...prev,
                              [section.id]: false
                            }));
                            setCommentTexts(prev => ({
                              ...prev,
                              [section.id]: ''
                            }));
                          }
                          if (e.key === 'Escape') setCommentInputs(prev => ({
                            ...prev,
                            [section.id]: false
                          }));
                        }} placeholder="Add a comment" className="flex-1 bg-transparent text-[#e8e4df] text-[13px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/20 pb-1" autoFocus data-id="element-956" /><button onClick={() => setCommentInputs(prev => ({
                          ...prev,
                          [section.id]: false
                        }))} className="text-[12px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-957">Send</button></div>}
                            </div>
                          </>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {doc.status !== 'final' && <div className="mt-8 flex items-center gap-6" data-id="element-958">
            <button className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors" data-id="element-959">Save to source</button>
            <button className="text-[13px] text-[#8a8680]/50 hover:text-[#8a8680] flex items-center gap-1.5" data-id="element-960"><Plus size={12} data-id="element-961" />Add section</button>
          </div>}
      </div>
    </motion.div>;
}